import React, { useMemo, useState } from "react";
import {
  Building2, Shield, Plus, Pencil, Users, UserCog, GitMerge,
  Search, X, Ban, Crown,
} from "lucide-react";
import AlertModal from "../../components/AlertModal";
import { useDepartmentsAdmin } from "./hooks/useDepartmentsAdmin";

const makeStyles = (theme, isRTL) => {
  const dark = theme === "dark";
  return {
    container: {
      padding: "24px",
      direction: isRTL ? "rtl" : "ltr",
      color: dark ? "#f1f5f9" : "#1e293b",
    },
    header: {
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      borderRadius: "24px",
      padding: "32px",
      marginBottom: "24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "16px",
      background: dark ? "rgba(15, 23, 42, 0.6)" : "rgba(255, 255, 255, 0.7)",
      border: dark ? "1px solid rgba(148, 163, 184, 0.15)" : "1px solid rgba(0,0,0,0.05)",
    },
    title: {
      fontSize: "28px", fontWeight: 700, margin: 0,
      display: "flex", alignItems: "center", gap: "12px",
      letterSpacing: "-0.5px",
    },
    subtitle: { fontSize: "15px", color: dark ? "#cbd5e1" : "#64748b", margin: "10px 0 0 0", fontWeight: 500 },
    card: {
      background: dark ? "rgba(15, 23, 42, 0.6)" : "rgba(255,255,255,0.85)",
      border: dark ? "1px solid rgba(148, 163, 184, 0.15)" : "1px solid rgba(0,0,0,0.06)",
      borderRadius: "20px",
      padding: "20px",
    },
    controls: { display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center", marginBottom: "18px" },
    input: {
      padding: "10px 14px", borderRadius: "12px", fontSize: "14px", outline: "none",
      background: dark ? "rgba(30, 41, 59, 0.8)" : "#fff",
      border: dark ? "1px solid rgba(148,163,184,0.25)" : "1px solid #e2e8f0",
      color: dark ? "#f1f5f9" : "#1e293b",
      minWidth: "180px",
    },
    btn: (variant = "primary", disabled = false) => ({
      display: "inline-flex", alignItems: "center", gap: "8px",
      padding: "10px 16px", borderRadius: "12px", fontSize: "14px", fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
      transition: "all 0.2s ease", border: "1px solid transparent",
      ...(variant === "primary" && {
        background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff",
      }),
      ...(variant === "ghost" && {
        background: "transparent",
        border: dark ? "1px solid rgba(148,163,184,0.25)" : "1px solid #e2e8f0",
        color: dark ? "#cbd5e1" : "#475569",
      }),
      ...(variant === "danger" && {
        background: dark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)",
        border: "1px solid rgba(239,68,68,0.35)", color: "#ef4444",
      }),
    }),
    table: { width: "100%", borderCollapse: "collapse", fontSize: "14px" },
    th: {
      textAlign: isRTL ? "right" : "left", padding: "12px", fontWeight: 600,
      color: dark ? "#94a3b8" : "#64748b", fontSize: "13px",
      borderBottom: dark ? "1px solid rgba(148,163,184,0.15)" : "1px solid #e2e8f0",
      whiteSpace: "nowrap",
    },
    td: {
      padding: "12px",
      borderBottom: dark ? "1px solid rgba(148,163,184,0.08)" : "1px solid #f1f5f9",
      verticalAlign: "middle",
    },
    chip: {
      display: "inline-flex", alignItems: "center", gap: "6px",
      padding: "4px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600,
      background: dark ? "rgba(59,130,246,0.15)" : "rgba(59,130,246,0.08)",
      color: dark ? "#93c5fd" : "#2563eb",
      margin: isRTL ? "0 0 4px 6px" : "0 6px 4px 0",
    },
    statusChip: (active) => ({
      display: "inline-block", padding: "4px 10px", borderRadius: "999px",
      fontSize: "12px", fontWeight: 700,
      background: active
        ? (dark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)")
        : (dark ? "rgba(148,163,184,0.15)" : "rgba(100,116,139,0.1)"),
      color: active ? "#22c55e" : (dark ? "#94a3b8" : "#64748b"),
    }),
    iconBtn: (color) => ({
      background: "transparent", border: "none", cursor: "pointer",
      padding: "6px", borderRadius: "8px", display: "inline-flex",
      color: color || (dark ? "#94a3b8" : "#64748b"),
    }),
    overlay: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
      backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 9999, padding: "20px",
    },
    modal: {
      background: dark ? "rgba(15, 23, 42, 0.97)" : "#fff",
      borderRadius: "20px", padding: "28px", width: "100%", maxWidth: "520px",
      maxHeight: "85vh", overflowY: "auto",
      border: dark ? "1px solid rgba(148,163,184,0.15)" : "1px solid rgba(0,0,0,0.05)",
      direction: isRTL ? "rtl" : "ltr",
      color: dark ? "#f1f5f9" : "#1e293b",
    },
    modalTitle: {
      fontSize: "20px", fontWeight: 700, margin: "0 0 18px 0",
      display: "flex", alignItems: "center", justifyContent: "space-between",
    },
    label: { display: "block", fontSize: "13px", fontWeight: 600, margin: "14px 0 6px", color: dark ? "#cbd5e1" : "#475569" },
    hint: { fontSize: "12.5px", color: dark ? "#94a3b8" : "#64748b", lineHeight: 1.6 },
    emptyState: { textAlign: "center", padding: "48px 20px", color: dark ? "#94a3b8" : "#64748b" },
  };
};

function DepartmentsPage() {
  const {
    theme, language, t, profileLoading, can,
    companies, selectedCompany, setSelectedCompany,
    departments, loading, showInactive, setShowInactive,
    alertModal, setAlertModal,
    createDepartment, updateDepartment, deactivateDepartment,
    mergeDepartments, setManagers, fetchMembers,
  } = useDepartmentsAdmin();

  const isRTL = language === "ar";
  const styles = useMemo(() => makeStyles(theme, isRTL), [theme, isRTL]);

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editModal, setEditModal] = useState(null);   // null | {mode:'create'} | {mode:'edit', dept}
  const [mergeModal, setMergeModal] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [membersModal, setMembersModal] = useState(null);  // {dept, members, loading}
  const [managersModal, setManagersModal] = useState(null); // {dept, list, suggestion}
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return departments;
    return departments.filter(
      (d) => d.name.toLowerCase().includes(q) || (d.nameAr || "").toLowerCase().includes(q)
    );
  }, [departments, search]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (err) {
      setAlertModal({ open: true, title: t.error, message: err.message, variant: "error" });
    } finally { setBusy(false); }
  };

  const openMembers = async (dept) => {
    setMembersModal({ dept, members: [], loading: true });
    try {
      const members = await fetchMembers(dept.id);
      setMembersModal({ dept, members, loading: false });
    } catch (err) {
      setMembersModal(null);
      setAlertModal({ open: true, title: t.error, message: err.message, variant: "error" });
    }
  };

  const openManagers = async (dept) => {
    let suggestion = null;
    let members = [];
    try { members = await fetchMembers(dept.id); } catch { /* suggestion is optional */ }
    const votes = new Map();
    for (const m of members) {
      if (!m.firstLineManagerInfinityCode) continue;
      const k = m.firstLineManagerInfinityCode;
      votes.set(k, { code: k, name: m.firstLineManagerName, n: (votes.get(k)?.n || 0) + 1 });
    }
    for (const v of votes.values()) if (!suggestion || v.n > suggestion.n) suggestion = v;
    setManagersModal({
      dept,
      list: (dept.managers || []).map((m) => ({ infinityCode: m.infinityCode, role: m.role })),
      suggestion,
      newCode: "",
    });
  };

  // ── gate ──
  if (profileLoading) {
    return <div style={styles.container}><div style={styles.emptyState}>{t.loading}</div></div>;
  }
  if (!can.view) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}><Shield size={28} />{t.adminOnly}</h1>
        </div>
        <div style={{ ...styles.card, ...styles.emptyState }}>
          <Shield size={64} style={{ opacity: 0.3, marginBottom: "1rem" }} />
          <div style={{ fontWeight: 600 }}>{t.unauthorized}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onConfirm={() => setAlertModal({ ...alertModal, open: false })}
        onClose={() => setAlertModal({ ...alertModal, open: false })}
        theme={theme}
        language={language}
      />

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}><Building2 size={28} />{t.title}</h1>
          <p style={styles.subtitle}>{t.subtitle}</p>
        </div>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {can.merge && (
            <button
              style={styles.btn("ghost", selectedIds.size < 2)}
              disabled={selectedIds.size < 2}
              title={t.mergeSelectHint}
              onClick={() => { setMergeTargetId(String([...selectedIds][0])); setMergeModal(true); }}
            >
              <GitMerge size={17} />{t.merge}{selectedIds.size >= 2 ? ` (${selectedIds.size})` : ""}
            </button>
          )}
          {can.add && (
            <button style={styles.btn("primary")} onClick={() => setEditModal({ mode: "create", name: "", nameAr: "", description: "" })}>
              <Plus size={17} />{t.newDepartment}
            </button>
          )}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.controls}>
          <select style={styles.input} value={selectedCompany} onChange={(e) => { setSelectedCompany(e.target.value); setSelectedIds(new Set()); }}>
            {!companies.length && <option value="">{t.company}</option>}
            {companies.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div style={{ position: "relative", flex: 1, minWidth: "200px" }}>
            <Search size={16} style={{ position: "absolute", top: "12px", [isRTL ? "right" : "left"]: "12px", opacity: 0.5 }} />
            <input
              style={{ ...styles.input, width: "100%", boxSizing: "border-box", padding: isRTL ? "10px 38px 10px 14px" : "10px 14px 10px 38px" }}
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13.5px", cursor: "pointer" }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            {t.showInactive}
          </label>
        </div>

        {!selectedCompany ? (
          <div style={styles.emptyState}>{t.selectCompanyFirst}</div>
        ) : loading ? (
          <div style={styles.emptyState}>{t.loading}</div>
        ) : !filtered.length ? (
          <div style={styles.emptyState}>{t.empty}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {can.merge && <th style={{ ...styles.th, width: "36px" }} />}
                  <th style={styles.th}>{t.name}</th>
                  <th style={styles.th}>{t.members}</th>
                  <th style={styles.th}>{t.managers}</th>
                  <th style={styles.th}>{t.status}</th>
                  <th style={styles.th}>{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.id}>
                    {can.merge && (
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(d.id)}
                          disabled={!d.isActive}
                          onChange={() => toggleSelect(d.id)}
                        />
                      </td>
                    )}
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600 }}>{d.name}</div>
                      {d.nameAr && <div style={{ fontSize: "12.5px", opacity: 0.7 }}>{d.nameAr}</div>}
                    </td>
                    <td style={styles.td}>
                      <button style={{ ...styles.chip, border: "none", cursor: "pointer" }} onClick={() => openMembers(d)}>
                        <Users size={13} />{t.membersCount(d._count?.employees ?? 0)}
                      </button>
                    </td>
                    <td style={styles.td}>
                      {(d.managers || []).length
                        ? d.managers.map((m) => (
                          <span key={m.id} style={styles.chip}>
                            {m.role === "MANAGER" ? <Crown size={12} /> : <UserCog size={12} />}
                            {m.employee?.arabicName || m.employee?.clientCode || m.employee?.infinityCode || m.infinityCode}
                          </span>
                        ))
                        : <span style={{ opacity: 0.5, fontSize: "13px" }}>{t.noManagers}</span>}
                    </td>
                    <td style={styles.td}><span style={styles.statusChip(d.isActive)}>{d.isActive ? t.active : t.inactive}</span></td>
                    <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                      {can.edit && (
                        <button style={styles.iconBtn()} title={t.rename}
                          onClick={() => setEditModal({ mode: "edit", dept: d, name: d.name, nameAr: d.nameAr || "", description: d.description || "" })}>
                          <Pencil size={17} />
                        </button>
                      )}
                      {can.assignManagers && (
                        <button style={styles.iconBtn()} title={t.managersTitle} onClick={() => openManagers(d)}>
                          <UserCog size={17} />
                        </button>
                      )}
                      {can.delete && d.isActive && (
                        <button style={styles.iconBtn("#ef4444")} title={t.deactivate} onClick={() => setConfirmDeactivate(d)}>
                          <Ban size={17} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* create / edit modal */}
      {editModal && (
        <div style={styles.overlay} onClick={() => !busy && setEditModal(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              {editModal.mode === "create" ? t.createTitle : t.editTitle}
              <button style={styles.iconBtn()} onClick={() => setEditModal(null)}><X size={20} /></button>
            </h2>
            <label style={styles.label}>{t.name}</label>
            <input style={{ ...styles.input, width: "100%", boxSizing: "border-box" }} value={editModal.name}
              onChange={(e) => setEditModal({ ...editModal, name: e.target.value })} />
            <label style={styles.label}>{t.nameAr}</label>
            <input style={{ ...styles.input, width: "100%", boxSizing: "border-box" }} dir="rtl" value={editModal.nameAr}
              onChange={(e) => setEditModal({ ...editModal, nameAr: e.target.value })} />
            <label style={styles.label}>{t.descriptionLabel}</label>
            <input style={{ ...styles.input, width: "100%", boxSizing: "border-box" }} value={editModal.description}
              onChange={(e) => setEditModal({ ...editModal, description: e.target.value })} />
            {editModal.mode === "edit" && editModal.name.trim() !== editModal.dept.name && (
              <p style={{ ...styles.hint, marginTop: "12px" }}>⚠ {t.renameCascadeNote}</p>
            )}
            <div style={{ display: "flex", gap: "10px", marginTop: "22px", justifyContent: "flex-end" }}>
              <button style={styles.btn("ghost")} disabled={busy} onClick={() => setEditModal(null)}>{t.cancel}</button>
              <button
                style={styles.btn("primary", busy || !editModal.name.trim())}
                disabled={busy || !editModal.name.trim()}
                onClick={() => run(async () => {
                  const payload = { name: editModal.name, nameAr: editModal.nameAr || null, description: editModal.description || null };
                  if (editModal.mode === "create") await createDepartment(payload);
                  else await updateDepartment(editModal.dept.id, payload);
                  setEditModal(null);
                })}
              >
                {t.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* merge modal */}
      {mergeModal && (
        <div style={styles.overlay} onClick={() => !busy && setMergeModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              {t.mergeTitle}
              <button style={styles.iconBtn()} onClick={() => setMergeModal(false)}><X size={20} /></button>
            </h2>
            <p style={styles.hint}>{t.mergeWarning}</p>
            <label style={styles.label}>{t.mergeInto}</label>
            <select style={{ ...styles.input, width: "100%", boxSizing: "border-box" }} value={mergeTargetId}
              onChange={(e) => setMergeTargetId(e.target.value)}>
              {[...selectedIds].map((id) => {
                const d = departments.find((x) => x.id === id);
                return d ? <option key={id} value={id}>{d.name} ({t.membersCount(d._count?.employees ?? 0)})</option> : null;
              })}
            </select>
            <div style={{ display: "flex", gap: "10px", marginTop: "22px", justifyContent: "flex-end" }}>
              <button style={styles.btn("ghost")} disabled={busy} onClick={() => setMergeModal(false)}>{t.cancel}</button>
              <button
                style={styles.btn("danger", busy || !mergeTargetId)}
                disabled={busy || !mergeTargetId}
                onClick={() => run(async () => {
                  const target = Number(mergeTargetId);
                  const sources = [...selectedIds].filter((id) => id !== target);
                  await mergeDepartments(target, sources);
                  setSelectedIds(new Set());
                  setMergeModal(false);
                })}
              >
                <GitMerge size={16} />{t.confirmMerge}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* members modal */}
      {membersModal && (
        <div style={styles.overlay} onClick={() => setMembersModal(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              {t.membersTitle} — {membersModal.dept.name}
              <button style={styles.iconBtn()} onClick={() => setMembersModal(null)}><X size={20} /></button>
            </h2>
            {membersModal.loading ? (
              <div style={styles.emptyState}>{t.loading}</div>
            ) : !membersModal.members.length ? (
              <div style={styles.emptyState}>{t.noMembers}</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{t.name}</th>
                    <th style={styles.th}>Employee Code</th>
                  </tr>
                </thead>
                <tbody>
                  {membersModal.members.map((m) => (
                    <tr key={m.id}>
                      <td style={styles.td}>{m.arabicName || "—"}</td>
                      <td style={styles.td}>{m.clientCode || m.infinityCode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* managers modal */}
      {managersModal && (
        <div style={styles.overlay} onClick={() => !busy && setManagersModal(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              {t.managersTitle} — {managersModal.dept.name}
              <button style={styles.iconBtn()} onClick={() => setManagersModal(null)}><X size={20} /></button>
            </h2>

            {managersModal.suggestion && !managersModal.list.some((m) => m.infinityCode === managersModal.suggestion.code) && (
              <div style={{ ...styles.card, padding: "12px 16px", marginBottom: "14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: 700 }}>
                    {t.suggested}: {managersModal.suggestion.name || managersModal.suggestion.code}
                  </div>
                  <div style={styles.hint}>{t.suggestedManagerHint}</div>
                </div>
                <button style={styles.btn("ghost")} onClick={() => setManagersModal({
                  ...managersModal,
                  list: [...managersModal.list, { infinityCode: managersModal.suggestion.code, role: "MANAGER" }],
                })}>
                  <Plus size={15} />{t.addManager}
                </button>
              </div>
            )}

            {managersModal.list.map((m, i) => (
              <div key={i} style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "10px" }}>
                <input style={{ ...styles.input, flex: 1 }} value={m.infinityCode} placeholder={t.managerCodePlaceholder}
                  onChange={(e) => {
                    const list = [...managersModal.list];
                    list[i] = { ...list[i], infinityCode: e.target.value };
                    setManagersModal({ ...managersModal, list });
                  }} />
                <select style={styles.input} value={m.role} onChange={(e) => {
                  const list = [...managersModal.list];
                  list[i] = { ...list[i], role: e.target.value };
                  setManagersModal({ ...managersModal, list });
                }}>
                  <option value="MANAGER">{t.manager}</option>
                  <option value="DEPUTY">{t.deputy}</option>
                </select>
                <button style={styles.iconBtn("#ef4444")} title={t.remove} onClick={() => setManagersModal({
                  ...managersModal, list: managersModal.list.filter((_, j) => j !== i),
                })}>
                  <X size={17} />
                </button>
              </div>
            ))}

            <button style={{ ...styles.btn("ghost"), marginTop: "6px" }} onClick={() => setManagersModal({
              ...managersModal, list: [...managersModal.list, { infinityCode: "", role: "MANAGER" }],
            })}>
              <Plus size={15} />{t.addManager}
            </button>

            <div style={{ display: "flex", gap: "10px", marginTop: "22px", justifyContent: "flex-end" }}>
              <button style={styles.btn("ghost")} disabled={busy} onClick={() => setManagersModal(null)}>{t.cancel}</button>
              <button style={styles.btn("primary", busy)} disabled={busy}
                onClick={() => run(async () => {
                  await setManagers(managersModal.dept.id, managersModal.list.filter((m) => m.infinityCode.trim()));
                  setManagersModal(null);
                })}>
                {t.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* deactivate confirm */}
      {confirmDeactivate && (
        <div style={styles.overlay} onClick={() => !busy && setConfirmDeactivate(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>{t.deactivate} — {confirmDeactivate.name}</h2>
            <p style={styles.hint}>{t.deactivateConfirm}</p>
            <div style={{ display: "flex", gap: "10px", marginTop: "22px", justifyContent: "flex-end" }}>
              <button style={styles.btn("ghost")} disabled={busy} onClick={() => setConfirmDeactivate(null)}>{t.cancel}</button>
              <button style={styles.btn("danger", busy)} disabled={busy}
                onClick={() => run(async () => {
                  await deactivateDepartment(confirmDeactivate.id);
                  setConfirmDeactivate(null);
                })}>
                <Ban size={16} />{t.deactivate}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DepartmentsPage;
